/**
 * GalaxyEntryButton — W49
 *
 * Context-aware Galaxy entry point — shared by both the top bar and side rail.
 * Reads current wouter location. If the path matches /account/:id, it routes to
 * /galaxy/:id (account-walk mode). Otherwise routes to /galaxy (global overview).
 *
 * Variants:
 *   "topbar"   — small square button with tooltip. Sits in OperatorTopbar.
 *   "siderail" — hero tile, 64px tall, thumbnail + "Galaxy" label bottom-left.
 *                Entire tile is clickable with a subtle hover lift.
 */

import { useLocation } from "wouter";
import GalaxyMiniThumbnail from "./GalaxyMiniThumbnail";

interface GalaxyEntryButtonProps {
  variant: "topbar" | "siderail";
  /** Whether the side rail is currently collapsed (icon-only mode). */
  collapsed?: boolean;
}

/** Extract accountId from /account/:id paths (singular, per canon routing). */
function useGalaxyDestination(): string {
  const [location] = useLocation();
  const match = location.match(/^\/account\/([^/]+)/);
  return match ? `/galaxy/${match[1]}` : "/galaxy";
}

// ─── Top bar variant ──────────────────────────────────────────────────────────

function GalaxyTopbarButton({ destination, navigate }: { destination: string; navigate: (to: string) => void }) {
  return (
    <button
      type="button"
      title="Galaxy"
      aria-label="Galaxy"
      onClick={() => navigate(destination)}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 36,
        height: 36,
        padding: 0,
        background: "transparent",
        border: "1px solid #E5E5E7",
        borderRadius: 6,
        cursor: "pointer",
        flexShrink: 0,
        overflow: "hidden",
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.borderColor = "#6366f1";
        el.style.boxShadow = "0 0 0 2px rgba(99,102,241,0.15)";
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.borderColor = "#E5E5E7";
        el.style.boxShadow = "none";
      }}
    >
      <GalaxyMiniThumbnail size={34} style={{ borderRadius: 5 }} />
    </button>
  );
}

// ─── Side rail hero tile variant ──────────────────────────────────────────────

function GalaxySiderailTile({
  destination,
  navigate,
  active,
  collapsed,
}: {
  destination: string;
  navigate: (to: string) => void;
  active: boolean;
  collapsed: boolean;
}) {
  if (collapsed) {
    // In collapsed mode: icon-only, matches SideRow collapsed style
    return (
      <button
        type="button"
        title="Galaxy"
        aria-label="Galaxy"
        onClick={() => navigate(destination)}
        className="group w-full flex items-center transition-colors relative"
        style={{
          gap: 0,
          padding: "8px 0",
          justifyContent: "center",
          background: active ? "#26262B" : "transparent",
          border: "none",
          cursor: "pointer",
          width: "100%",
          position: "relative",
        }}
        onMouseEnter={(e) => {
          if (!active) (e.currentTarget as HTMLButtonElement).style.background = "rgba(31,31,35,0.55)";
        }}
        onMouseLeave={(e) => {
          if (!active) (e.currentTarget as HTMLButtonElement).style.background = "transparent";
        }}
      >
        {active && (
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              left: 0, top: 0, bottom: 0,
              width: 3,
              background: "#DC2626",
            }}
          />
        )}
        <GalaxyMiniThumbnail size={22} style={{ borderRadius: 4, opacity: active ? 1 : 0.75 }} />
      </button>
    );
  }

  // Expanded hero tile
  return (
    <button
      type="button"
      aria-label="Galaxy"
      onClick={() => navigate(destination)}
      style={{
        display: "block",
        position: "relative",
        width: "calc(100% - 16px)",
        margin: "4px 8px 2px",
        height: 64,
        borderRadius: 8,
        overflow: "hidden",
        border: active ? "1.5px solid rgba(99,102,241,0.5)" : "1px solid rgba(255,255,255,0.06)",
        cursor: "pointer",
        padding: 0,
        background: "transparent",
        transition: "transform 0.15s, box-shadow 0.15s",
        boxShadow: active ? "0 0 0 1px rgba(99,102,241,0.2)" : "none",
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.transform = "translateY(-1px)";
        el.style.boxShadow = "0 4px 12px rgba(0,0,0,0.4)";
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.transform = "translateY(0)";
        el.style.boxShadow = active ? "0 0 0 1px rgba(99,102,241,0.2)" : "none";
      }}
    >
      {/* Full-bleed thumbnail */}
      <GalaxyMiniThumbnail
        size="large"
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 0,
          position: "absolute",
          inset: 0,
        }}
      />

      {/* "Galaxy" label — bottom-left overlay */}
      <span
        style={{
          position: "absolute",
          bottom: 7,
          left: 9,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: "0.06em",
          color: "#FFFFFF",
          textShadow: "0 1px 3px rgba(0,0,0,0.8)",
          fontFamily: "Inter, system-ui, sans-serif",
          pointerEvents: "none",
          userSelect: "none",
          textTransform: "uppercase",
        }}
      >
        Galaxy
      </span>

      {/* Active left accent */}
      {active && (
        <span
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 0, top: 0, bottom: 0,
            width: 3,
            background: "#DC2626",
            borderRadius: "8px 0 0 8px",
          }}
        />
      )}
    </button>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export default function GalaxyEntryButton({ variant, collapsed = false }: GalaxyEntryButtonProps) {
  const [location, navigate] = useLocation();
  const destination = useGalaxyDestination();
  const active = location.startsWith("/galaxy");

  if (variant === "topbar") {
    return <GalaxyTopbarButton destination={destination} navigate={navigate} />;
  }

  return (
    <GalaxySiderailTile
      destination={destination}
      navigate={navigate}
      active={active}
      collapsed={collapsed}
    />
  );
}
